import React, { useState, useRef, useEffect, useId } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { EASE_OUT } from '@/lib/motion';

/**
 * Module-level "any tooltip recently shown" timestamp. When the user closes
 * one tooltip and hovers another within REHOVER_GRACE_MS, the next tooltip
 * appears instantly — matching the toolbar/menu cohort behavior popularized
 * by Apple, Linear, and Raycast.
 */
const REHOVER_GRACE_MS = 800;
let lastTooltipClosedAt = 0;

export interface InfoTooltipProps {
  /** Content rendered inside the popover. */
  content: React.ReactNode;
  /** Optional override for the trigger icon. */
  ariaLabel?: string;
  /** Side to anchor the popover. Defaults to 'top'. */
  side?: 'top' | 'bottom';
}

function InfoIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
      <path d="M12 11v5M12 7.75v.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Lightweight info popover. Opens on hover, focus, or click; dismisses on
 * blur or click-outside. Used for short helper copy that would otherwise
 * crowd the layout.
 */
export default function InfoTooltip({
  content,
  ariaLabel = 'More info',
  side = 'top',
}: InfoTooltipProps) {
  const [open, setOpen] = useState(false);
  const [skipAnimation, setSkipAnimation] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const popoverId = useId();

  // When opening, decide whether to skip the entrance animation based on the
  // shared timestamp. When closing, record it so the next sibling can decide.
  const handleOpen = (next: boolean) => {
    if (next) {
      setSkipAnimation(Date.now() - lastTooltipClosedAt < REHOVER_GRACE_MS);
    } else {
      lastTooltipClosedAt = Date.now();
    }
    setOpen(next);
  };

  useEffect(() => {
    if (!open) return;
    const handleClick = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) handleOpen(false);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  return (
    <span ref={wrapperRef} className="relative inline-flex items-center">
      <button
        type="button"
        aria-label={ariaLabel}
        aria-describedby={open ? popoverId : undefined}
        aria-expanded={open}
        onMouseEnter={() => handleOpen(true)}
        onMouseLeave={() => handleOpen(false)}
        onFocus={() => handleOpen(true)}
        onBlur={() => handleOpen(false)}
        onClick={() => handleOpen(!open)}
        className="inline-flex items-center justify-center w-5 h-5 rounded-full text-text-tertiary hover:text-primary-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 transition-colors"
      >
        <InfoIcon />
      </button>

      <AnimatePresence>
        {open && (
          <motion.span
            id={popoverId}
            role="tooltip"
            initial={skipAnimation ? false : { opacity: 0, scale: 0.97, y: side === 'top' ? 4 : -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: side === 'top' ? 4 : -4 }}
            transition={{ duration: skipAnimation ? 0 : 0.15, ease: EASE_OUT }}
            className={`
              absolute left-1/2 -translate-x-1/2 z-50 px-3 py-2 rounded-lg
              bg-background-primary border border-border-primary/40
              shadow-lg pointer-events-none
              ${side === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'}
            `}
            style={{
              minWidth: 200,
              transformOrigin: side === 'top' ? 'bottom center' : 'top center',
            }}
          >
            {content}
          </motion.span>
        )}
      </AnimatePresence>
    </span>
  );
}
